import { fromDate, Time, toTime } from '@internationalized/date'
import * as React from 'react'
import { DateField, DateInput, DateSegment, TimeField } from 'react-aria-components'
import { cn } from '../../utils/cn'
import { Separator } from '../separator'
import { useDateTimePickerContext } from './date-time-picker-context'

export function DateTimePickerInput({
  className,
  timeFormat,
  ...props
}: DateTimePickerInput.Props) {
  const { value, setDateValue, setTimeValue } = useDateTimePickerContext()

  const [timezone] = React.useState(Intl.DateTimeFormat().resolvedOptions().timeZone)
  const dateValue = React.useMemo(() => {
    if (!value) return undefined
    return fromDate(value, timezone)
  }, [value, timezone])
  const timeValue = React.useMemo(() => {
    if (!value) return undefined
    return toTime(fromDate(value, timezone))
  }, [value])

  return (
    <div
      className={cn(
        'flex h-8 items-center gap-3 rounded-md px-3 ring ring-inset ring-gray-200',
        'focus-within:ring-accent-600 focus-within:outline-none focus-within:ring-2',
        className
      )}
      {...props}
    >
      <DateField
        className="flex-1 text-sm"
        granularity="day"
        hideTimeZone
        value={dateValue}
        onChange={(value) => {
          if (!value) return
          setDateValue(value.toDate())
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <DateInput>
          {(segment) => (
            <DateSegment
              className={cn(
                'cursor-default rounded-sm p-0.5 data-[type=literal]:p-0 data-[placeholder]:text-gray-400',
                'focus-visible:bg-gray-100 focus-visible:outline-none'
              )}
              segment={segment}
            />
          )}
        </DateInput>
      </DateField>
      <Separator className="h-3.5" orientation="vertical" />
      <TimeField
        className="flex-1 text-sm"
        hideTimeZone
        hourCycle={
          timeFormat &&
          {
            '12h': 12 as const,
            '24h': 24 as const,
          }[timeFormat]
        }
        value={timeValue}
        onChange={(value) => {
          if (!value) return
          if (!(value instanceof Time)) return
          setTimeValue(value)
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <DateInput>
          {(segment) => (
            <DateSegment
              className={cn(
                'cursor-default rounded-sm p-0.5 data-[type=literal]:p-0 data-[placeholder]:text-gray-400',
                'focus-visible:bg-gray-100 focus-visible:outline-none'
              )}
              segment={segment}
            />
          )}
        </DateInput>
      </TimeField>
    </div>
  )
}

export namespace DateTimePickerInput {
  export interface Props extends React.ComponentProps<'div'> {
    timeFormat?: '12h' | '24h'
  }
}
